import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Box, Button, Card, CardContent, CircularProgress, Container, Dialog, DialogActions, DialogContent, DialogTitle, Divider, Radio, TextField, Typography, useMediaQuery, Checkbox, FormControlLabel, CssBaseline } from '@mui/material';

import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import ScheduleIcon from "@mui/icons-material/Schedule";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import PhoneIcon from "@mui/icons-material/Phone";
import PaymentsIcon from "@mui/icons-material/Payments";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { ThemeProvider as CustomThemeProvider, useTheme } from "../contexts/ThemeContext";
import { useSystemButtonStyles } from "../hooks/useSystemButtonStyles";
import AuthPageToggleBar from "../components/AuthPageToggleBar";
import { createInvoiceCheckout, fetchPublicInvoice, selectInvoiceMembershipPlan, clearInvoicePlanSelection, confirmInvoiceBreakdown, submitInvoiceOptOut, submitMembershipInvoiceOptOut, verifyInvoiceCheckout } from "../utils/api/api";
import { APP_FONT_FAMILY } from "../constants/typography";
import newEraLogo from "../NewEraLogo.png";
import { getMm } from "../theme/modernMaterialEnterprise";
import i18n from "../utils/language/i18n";
import { formatClubMoney } from "../utils/clubBillingCurrency";
import { usePublicPageLanguage } from "../hooks/usePublicPageLanguage";

const LOGO_LIGHT = `${process.env.PUBLIC_URL || ""}/NewEraBlackLogo.png`;
const LOGO_DARK = newEraLogo;
const PAYMENT_LOGO_BASE = `${process.env.PUBLIC_URL || ""}/payment-logos`;

const PAYMENT_BRAND_LOGOS = [
  { src: `${PAYMENT_LOGO_BASE}/visa.svg`, alt: "Visa" },
  { src: `${PAYMENT_LOGO_BASE}/mastercard.svg`, alt: "Mastercard" },
  { src: `${PAYMENT_LOGO_BASE}/klarna.svg`, alt: "Klarna" },
];

const FONT = APP_FONT_FAMILY;

/** select-plan / confirm-breakdown responses omit payment extras — keep them from the last full GET. */
function mergePublicInvoice(prev, next) {
  if (!next) return prev;
  if (!prev) return next;
  return {
    ...prev,
    ...next,
    hasInvoiceTerms: next.hasInvoiceTerms ?? prev.hasInvoiceTerms,
    paymentMethods: next.paymentMethods ?? prev.paymentMethods,
    stripeOnlyPayment: next.stripeOnlyPayment ?? prev.stripeOnlyPayment,
    subscriptionCheckout: next.subscriptionCheckout ?? prev.subscriptionCheckout,
    clubName: next.clubName ?? prev.clubName,
    memberName: next.memberName ?? prev.memberName,
    memberEmail: next.memberEmail ?? prev.memberEmail,
  };
}

function PaymentBrandLogos({ isDarkMode }) {
  const badgeBorder = `1px solid ${getMm(isDarkMode).outlineVariant}`;

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        flexWrap: "wrap",
        mt: 1,
      }}
      aria-label="Visa, Mastercard, Klarna"
    >
      {PAYMENT_BRAND_LOGOS.map(({ src, alt }) => (
        <Box
          key={alt}
          sx={{
            width: 56,
            height: 36,
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: "6px",
            border: badgeBorder,
            bgcolor: "#FFFFFF",
            overflow: "hidden",
            p: 0.5,
          }}
        >
          <Box
            component="img"
            src={src}
            alt={alt}
            sx={{
              display: "block",
              maxWidth: "100%",
              maxHeight: "100%",
              width: "auto",
              height: "auto",
              objectFit: "contain",
            }}
          />
        </Box>
      ))}
    </Box>
  );
}

const DATE_LOCALE_BY_LANG = {
  en: "en-GB",
  sv: "sv-SE",
  pt: "pt-BR",
  es: "es-ES",
};

function formatMoney(amount, currency = "SEK") {
  return formatClubMoney((key, opts) => i18n.t(key, opts), i18n.language, amount, currency);
}

function formatDate(iso) {
  if (!iso) return "—";
  try {
    const lang = String(i18n.language || "sv").split("-")[0];
    const locale = DATE_LOCALE_BY_LANG[lang] || lang;
    return new Date(iso).toLocaleDateString(locale, { year: "numeric", month: "long", day: "numeric" });
  } catch {
    return iso;
  }
}

function isMonthlyPlanId(planId) {
  return ["monthly", "monthly_1", "monthly_3", "monthly_6", "quarterly", "semi_annual"].includes(
    String(planId || "")
  );
}

function isTermPlanId(planId) {
  return planId === "term" || planId === "annual";
}

function planPaymentModeHint(planId, t) {
  if (isMonthlyPlanId(planId)) {
    return t("invoice_plan_hint_monthly");
  }
  if (isTermPlanId(planId)) {
    return t("invoice_plan_hint_term");
  }
  return null;
}

function invoicePlanLabelKey(id) {
  const periodKeys = {
    term: "invoice_billing_plan_term",
    monthly_1: "invoice_billing_plan_monthly_1",
    monthly_3: "invoice_billing_plan_monthly_3",
    monthly_6: "invoice_billing_plan_monthly_6",
  };
  if (periodKeys[id]) return periodKeys[id];
  const keys = {
    monthly: "finance_membership_plan_monthly",
    quarterly: "finance_membership_plan_quarterly",
    semi_annual: "finance_membership_plan_semi_annual",
    // enableTermBilling → annual plan; same label as period "Termin" on HT/VT invoices
    annual: "invoice_billing_plan_term",
  };
  return keys[id] || id;
}

function InvoiceShell({ children, maxWidth = 640 }) {
  const { isDarkMode } = useTheme();
  const isMobile = useMediaQuery("(max-width:600px)");

  return (
    <>
      <CssBaseline />
      <Box
        sx={{
          minHeight: { xs: "100dvh", sm: "100vh" },
          width: "100%",
          bgcolor: getMm(isDarkMode).background,
          fontFamily: FONT,
        }}
      >
        <Container
          maxWidth="md"
          sx={{
            minHeight: "100vh",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            py: { xs: 3, sm: 5 },
            px: 2,
            position: "relative",
          }}
        >
          <AuthPageToggleBar showOnlyOnLogin={false} />

          <Box
            sx={{
              width: "100%",
              maxWidth,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <Box sx={{ textAlign: "center", mb: { xs: 2.5, sm: 3.5 }, width: "100%" }}>
              <Box
                component="img"
                src={isDarkMode ? LOGO_DARK : LOGO_LIGHT}
                alt="NewEra"
                sx={{
                  maxWidth: isMobile ? "220px" : "280px",
                  width: "100%",
                  height: "auto",
                  display: "block",
                  mx: "auto",
                }}
              />
            </Box>
            {children}
          </Box>

          <Typography
            sx={{
              mt: 3,
              fontSize: "0.75rem",
              color: getMm(isDarkMode).onSurfaceVariant,
              textAlign: "center",
              fontFamily: FONT,
            }}
          >
            NewEra · {new Date().getFullYear()}
          </Typography>
        </Container>
      </Box>
    </>
  );
}

function StatusCard({ icon, title, body, action }) {
  const { isDarkMode } = useTheme();

  return (
    <Card
      sx={{
        width: "100%",
        borderRadius: "8px",
        boxShadow: "none",
        border: `1px solid ${getMm(isDarkMode).outlineVariant}`,
        bgcolor: getMm(isDarkMode).surfaceLowest,
        fontFamily: FONT,
      }}
    >
      <CardContent sx={{ textAlign: "center", py: { xs: 4, sm: 5 }, px: { xs: 2.5, sm: 4 } }}>
        {icon}
        <Typography
          sx={{
            fontWeight: 700,
            fontSize: { xs: "1.25rem", sm: "1.5rem" },
            color: getMm(isDarkMode).onSurface,
            mb: 1,
            fontFamily: FONT,
          }}
        >
          {title}
        </Typography>
        <Typography
          sx={{
            color: getMm(isDarkMode).onSurfaceVariant,
            mb: 3,
            maxWidth: 420,
            mx: "auto",
            lineHeight: 1.6,
            fontSize: "0.9375rem",
            fontFamily: FONT,
          }}
        >
          {body}
        </Typography>
        {action}
      </CardContent>
    </Card>
  );
}

function InfoBanner({ variant, children }) {
  const { isDarkMode } = useTheme();
  const styles = {
    paid: {
      bg: isDarkMode ? "rgba(76, 175, 80, 0.12)" : "#ECFDF5",
      border: isDarkMode ? "#4CAF50" : "#A7F3D0",
      color: isDarkMode ? "#86EFAC" : "#065F46",
      icon: <CheckCircleIcon sx={{ color: "#4CAF50", fontSize: 22 }} />,
    },
    expired: {
      bg: isDarkMode ? "rgba(239, 68, 68, 0.1)" : "#FEF2F2",
      border: isDarkMode ? "#EF4444" : "#FECACA",
      color: getMm(isDarkMode).error,
      icon: <ScheduleIcon sx={{ color: "#EF4444", fontSize: 22 }} />,
    },
    active: {
      bg: getMm(isDarkMode).surfaceLow,
      border: getMm(isDarkMode).outlineVariant,
      color: getMm(isDarkMode).onSurfaceVariant,
      icon: <ScheduleIcon sx={{ color: getMm(isDarkMode).onSurfaceVariant, fontSize: 22 }} />,
    },
  };
  const s = styles[variant] || styles.active;

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "flex-start",
        gap: 1.25,
        bgcolor: s.bg,
        border: `2px solid ${s.border}`,
        borderRadius: "8px",
        p: 1.5,
        mb: 2.5,
      }}
    >
      {s.icon}
      <Typography sx={{ color: s.color, fontWeight: 600, fontSize: "0.875rem", lineHeight: 1.5, fontFamily: FONT }}>
        {children}
      </Typography>
    </Box>
  );
}

function LineItemRow({ line, currency, isDarkMode, isLast }) {
  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "flex-start",
        gap: 2,
        py: 1.25,
        borderBottom: isLast ? "none" : `1px solid ${getMm(isDarkMode).outlineVariant}`,
      }}
    >
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography sx={{ fontWeight: 500, color: getMm(isDarkMode).onSurfaceVariant, fontSize: "0.9375rem", fontFamily: FONT }}>
          {line.description}
        </Typography>
        {line.groupName && line.description !== line.groupName && (
          <Typography sx={{ fontSize: "0.75rem", color: isDarkMode ? "#9CA3AF" : "#9CA3AF", mt: 0.25, fontFamily: FONT }}>
            {line.groupName}
          </Typography>
        )}
      </Box>
      <Typography
        sx={{
          fontWeight: 600,
          color: getMm(isDarkMode).onSurface,
          whiteSpace: "nowrap",
          fontSize: "0.9375rem",
          fontFamily: FONT,
        }}
      >
        {formatMoney(line.amount, line.currency || currency)}
      </Typography>
    </Box>
  );
}

function resolveInvoiceLineGroups(invoice) {
  const trainingItems =
    invoice?.trainingLineItems?.length
      ? invoice.trainingLineItems
      : (invoice?.lineItems || []).filter(
          (line) =>
            line.code !== "club_term_fee" &&
            line.code !== "subscription_billing_meta" &&
            line.code !== "term_billing_meta"
        );
  const feeItems =
    invoice?.termFeeLineItems?.length
      ? invoice.termFeeLineItems
      : (invoice?.lineItems || []).filter((line) => line.code === "club_term_fee");
  const hasStructured =
    Boolean(invoice?.trainingLineItems?.length || invoice?.termFeeLineItems?.length) &&
    (trainingItems.length > 0 || feeItems.length > 0);
  return { trainingItems, feeItems, hasStructured };
}

function formatBillingPeriodEnd(iso) {
  if (!iso) return null;
  const datePart = String(iso).slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(datePart)) return formatDate(iso);
  return formatDate(`${datePart}T12:00:00.000Z`);
}

function InvoiceTermProrationBanner({ invoice, isDarkMode, variant = "payment" }) {
  const { t } = useTranslation();
  if (invoice.selectedPlanKind !== "term" || !invoice.termProrationApplied) return null;

  const cardBorder = `1px solid ${getMm(isDarkMode).outlineVariant}`;
  const muted = getMm(isDarkMode).onSurfaceVariant;
  const text = getMm(isDarkMode).onSurface;
  const periodEnd = formatBillingPeriodEnd(invoice.periodEnd);
  const key =
    variant === "breakdown" ? "invoice_breakdown_term_proration_note" : "invoice_term_proration_note";

  return (
    <Box
      sx={{
        mb: 2,
        p: 2,
        borderRadius: "8px",
        border: cardBorder,
        bgcolor: getMm(isDarkMode).surfaceLow,
      }}
    >
      <Typography sx={{ color: text, fontSize: "0.875rem", lineHeight: 1.55, fontFamily: FONT, mb: 1 }}>
        {t(key, {
          full: formatMoney(invoice.termProrationFullAmount, invoice.currency),
          due: formatMoney(invoice.amount, invoice.currency),
          days: invoice.termProrationRemainingDays,
          dayBasis: invoice.termProrationDayBasis,
          periodEnd: periodEnd || "—",
        })}
      </Typography>
      <Typography sx={{ color: muted, fontSize: "0.8125rem", lineHeight: 1.5, fontFamily: FONT }}>
        {t("invoice_term_proration_formula_hint")}
      </Typography>
    </Box>
  );
}

function InvoiceSubscriptionSummaryBanner({ invoice, isDarkMode, variant = "payment" }) {
  const { t } = useTranslation();
  const isMonthly =
    invoice.selectedPlanKind === "monthly" || Boolean(invoice.subscriptionCheckout);
  const recurring = invoice.subscriptionRecurringMonthlyAmount;
  if (!isMonthly || recurring == null) return null;

  const cardBorder = `1px solid ${getMm(isDarkMode).outlineVariant}`;
  const muted = getMm(isDarkMode).onSurfaceVariant;
  const text = getMm(isDarkMode).onSurface;
  const periodEnd = formatBillingPeriodEnd(invoice.periodEnd);
  const key =
    variant === "breakdown"
      ? "invoice_breakdown_proration_note"
      : "invoice_payment_subscription_summary";

  return (
    <Box
      sx={{
        mb: 2,
        p: 2,
        borderRadius: "8px",
        border: cardBorder,
        bgcolor: getMm(isDarkMode).surfaceLow,
      }}
    >
      <Typography sx={{ color: text, fontSize: "0.875rem", lineHeight: 1.55, fontFamily: FONT }}>
        {t(key, {
          first: formatMoney(invoice.amount, invoice.currency),
          dueToday: formatMoney(invoice.amount, invoice.currency),
          recurring: formatMoney(recurring, invoice.currency),
          periodEnd: periodEnd || "—",
        })}
      </Typography>
    </Box>
  );
}

function InvoiceItemizedCharges({ invoice, isDarkMode, showTotal = true }) {
  const { t } = useTranslation();
  const { trainingItems, feeItems, hasStructured } = resolveInvoiceLineGroups(invoice);
  const cardBorder = `1px solid ${getMm(isDarkMode).outlineVariant}`;
  const muted = getMm(isDarkMode).onSurfaceVariant;
  const text = getMm(isDarkMode).onSurface;
  const isMonthly = invoice.selectedPlanKind === "monthly";
  const recurring = invoice.subscriptionRecurringMonthlyAmount;

  if (!hasStructured) {
    const flatItems = (invoice.lineItems || []).filter(
      (line) => line.code !== "subscription_billing_meta" && line.code !== "term_billing_meta"
    );
    return (
      <Box
        sx={{
          border: cardBorder,
          borderRadius: "8px",
          px: 2,
          py: 0.5,
          mb: 2,
          bgcolor: getMm(isDarkMode).surfaceLowest,
        }}
      >
        {flatItems.length === 0 ? (
          <LineItemRow
            line={{ description: t("invoice_title"), amount: invoice.amount }}
            currency={invoice.currency}
            isDarkMode={isDarkMode}
            isLast={!showTotal}
          />
        ) : (
          flatItems.map((line, idx) => (
            <LineItemRow
              key={`flat-${idx}`}
              line={line}
              currency={invoice.currency}
              isDarkMode={isDarkMode}
              isLast={!showTotal && idx === flatItems.length - 1}
            />
          ))
        )}
        {showTotal ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              py: 1.5,
              mt: 0.5,
              borderTop: `2px solid ${getMm(isDarkMode).outlineVariant}`,
            }}
          >
            <Typography sx={{ fontWeight: 700, color: text, fontSize: "1rem", fontFamily: FONT }}>
              {t("invoice_total_due_now")}
            </Typography>
            <Typography sx={{ fontWeight: 800, color: text, fontSize: "1.25rem", fontFamily: FONT }}>
              {formatMoney(invoice.amount, invoice.currency)}
            </Typography>
          </Box>
        ) : null}
      </Box>
    );
  }

  return (
    <>
      {trainingItems.length > 0 ? (
        <>
          <Typography sx={{ fontWeight: 600, color: text, mb: 1, fontSize: "0.875rem", fontFamily: FONT }}>
            {t("invoice_breakdown_training_section")}
          </Typography>
          {isMonthly && recurring != null ? (
            <Typography sx={{ color: muted, fontSize: "0.8125rem", mb: 1, fontFamily: FONT, lineHeight: 1.5 }}>
              {t("invoice_breakdown_training_prorated_hint", {
                recurring: formatMoney(recurring, invoice.currency),
              })}
            </Typography>
          ) : null}
          <Box
            sx={{
              border: cardBorder,
              borderRadius: "8px",
              px: 2,
              py: 0.5,
              mb: 2,
              bgcolor: getMm(isDarkMode).surfaceLowest,
            }}
          >
            {trainingItems.map((line, idx) => (
              <LineItemRow
                key={`train-${idx}`}
                line={line}
                currency={invoice.currency}
                isDarkMode={isDarkMode}
                isLast={idx === trainingItems.length - 1}
              />
            ))}
          </Box>
        </>
      ) : null}

      {feeItems.length > 0 ? (
        <>
          <Typography sx={{ fontWeight: 600, color: text, mb: 1, fontSize: "0.875rem", fontFamily: FONT }}>
            {t("invoice_breakdown_fees_section")}
          </Typography>
          <Typography sx={{ color: muted, fontSize: "0.8125rem", mb: 1, fontFamily: FONT, lineHeight: 1.5 }}>
            {isMonthly
              ? t("invoice_breakdown_fees_once_on_first_monthly_invoice")
              : t("invoice_breakdown_fees_once_per_term")}
          </Typography>
          <Box
            sx={{
              border: cardBorder,
              borderRadius: "8px",
              px: 2,
              py: 0.5,
              mb: 2,
              bgcolor: getMm(isDarkMode).surfaceLowest,
            }}
          >
            {feeItems.map((line, idx) => (
              <LineItemRow
                key={`fee-${line.feeId || idx}`}
                line={{
                  ...line,
                  description: line.feeName || line.description,
                }}
                currency={invoice.currency}
                isDarkMode={isDarkMode}
                isLast={idx === feeItems.length - 1}
              />
            ))}
          </Box>
        </>
      ) : null}

      {showTotal ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            py: 1.5,
            mb: 2,
            borderTop: `2px solid ${getMm(isDarkMode).outlineVariant}`,
          }}
        >
          <Typography sx={{ fontWeight: 700, color: text, fontFamily: FONT }}>{t("invoice_total_due_now")}</Typography>
          <Typography sx={{ fontWeight: 800, color: text, fontSize: "1.25rem", fontFamily: FONT }}>
            {formatMoney(invoice.amount, invoice.currency)}
          </Typography>
        </Box>
      ) : null}
    </>
  );
}

function InvoiceOptOutSection({ invoice, token, onOptOutComplete }) {
  const { t } = useTranslation();
  const { isDarkMode } = useTheme();
  const [dialog, setDialog] = useState(null);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const options = (invoice.avanmalanOptions || []).filter((o) => !o.alreadyOptedOut);
  if (options.length === 0) return null;

  const cardBorder = `1px solid ${getMm(isDarkMode).outlineVariant}`;
  const muted = getMm(isDarkMode).onSurfaceVariant;
  const text = getMm(isDarkMode).onSurface;

  const openDialog = (option) => {
    setDialog(option);
    setComment("");
    setError(null);
  };

  const closeDialog = () => {
    if (submitting) return;
    setDialog(null);
    setComment("");
    setError(null);
  };

  const handleSubmit = async () => {
    if (!dialog || !token) return;
    setSubmitting(true);
    setError(null);
    try {
      const result = await submitInvoiceOptOut(token, dialog.groupId, comment);
      setSuccess(
        t("invoice_opt_out_success", {
          group: result.groupName || dialog.groupName || dialog.groupId,
        })
      );
      setDialog(null);
      setComment("");
      if (typeof onOptOutComplete === "function") {
        await onOptOutComplete();
      }
    } catch (err) {
      setError(err?.response?.data?.error || err.message || t("invoice_opt_out_error"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Divider sx={{ borderColor: getMm(isDarkMode).outlineVariant, my: 2.5 }} />
      <Typography sx={{ fontWeight: 700, color: text, mb: 1, fontSize: "1rem", fontFamily: FONT }}>
        {t("invoice_opt_out_section_title")}
      </Typography>
      <Typography sx={{ color: muted, fontSize: "0.875rem", mb: 2, fontFamily: FONT }}>
        {t("invoice_opt_out_section_intro")}
      </Typography>
      {success ? (
        <InfoBanner variant="paid">{success}</InfoBanner>
      ) : null}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
        {options.map((option) => {
          const label =
            option.buttonText?.trim() ||
            t("finance_notices_avanmalan_button_default");
          return (
            <Button
              key={option.groupId}
              variant="outlined"
              fullWidth
              onClick={() => openDialog(option)}
              sx={{
                borderRadius: "8px",
                py: 1.25,
                textTransform: "none",
                fontWeight: 600,
                fontFamily: FONT,
                borderColor: getMm(isDarkMode).outline,
                color: text,
                justifyContent: "flex-start",
              }}
            >
              {option.groupName ? `${label} (${option.groupName})` : label}
            </Button>
          );
        })}
      </Box>

      <Dialog
        open={Boolean(dialog)}
        onClose={closeDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "8px",
            bgcolor: getMm(isDarkMode).surfaceLowest,
            border: cardBorder,
            fontFamily: FONT,
          },
        }}
      >
        <DialogTitle sx={{ color: text, fontFamily: FONT, fontWeight: 700 }}>
          {dialog?.buttonText?.trim() || t("finance_notices_avanmalan_button_default")}
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ color: muted, fontSize: "0.875rem", mb: 2, fontFamily: FONT }}>
            {t("invoice_opt_out_confirm_body", {
              group: dialog?.groupName || dialog?.groupId || "",
            })}
          </Typography>
          {dialog?.commentMode !== "NONE" ? (
            <TextField
              fullWidth
              multiline
              minRows={3}
              label={
                dialog?.commentMode === "REQUIRED"
                  ? t("invoice_opt_out_comment_required")
                  : t("invoice_opt_out_comment_optional")
              }
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              required={dialog?.commentMode === "REQUIRED"}
              sx={{
                "& .MuiOutlinedInput-root": { borderRadius: "8px", fontFamily: FONT },
                "& .MuiInputLabel-root": { fontFamily: FONT },
              }}
            />
          ) : null}
          {error ? (
            <Typography sx={{ color: "#EF4444", fontSize: "0.875rem", mt: 2, fontFamily: FONT }}>
              {error}
            </Typography>
          ) : null}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button onClick={closeDialog} disabled={submitting} sx={{ textTransform: "none", fontFamily: FONT }}>
            {t("cancel")}
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={submitting || (dialog?.commentMode === "REQUIRED" && !comment.trim())}
            variant="contained"
            sx={{
              bgcolor: "#374151",
              textTransform: "none",
              fontFamily: FONT,
              boxShadow: "none",
              "&:hover": { bgcolor: "#374151", opacity: 0.92 },
            }}
          >
            {submitting ? t("loading") : t("invoice_opt_out_confirm")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

function MembershipInvoiceOptOutSection({ token, onOptOutComplete }) {
  const { t } = useTranslation();
  const { isDarkMode } = useTheme();
  const { destructiveOutlineButtonSx, cancelButtonSx } = useSystemButtonStyles();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const cardBorder = `1px solid ${getMm(isDarkMode).outlineVariant}`;
  const muted = getMm(isDarkMode).onSurfaceVariant;
  const text = getMm(isDarkMode).onSurface;

  const closeDialog = () => {
    if (submitting) return;
    setDialogOpen(false);
    setError(null);
  };

  const handleSubmit = async () => {
    if (!token) return;
    setSubmitting(true);
    setError(null);
    try {
      await submitMembershipInvoiceOptOut(token);
      setDialogOpen(false);
      if (typeof onOptOutComplete === "function") {
        await onOptOutComplete();
      }
    } catch (err) {
      setError(err?.response?.data?.error || err.message || t("invoice_membership_opt_out_error"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Divider sx={{ borderColor: getMm(isDarkMode).outlineVariant, my: 2.5 }} />
      <Typography sx={{ fontWeight: 700, color: text, mb: 1, fontSize: "1rem", fontFamily: FONT }}>
        {t("invoice_membership_opt_out_section_title")}
      </Typography>
      <Typography sx={{ color: muted, fontSize: "0.875rem", mb: 2, fontFamily: FONT }}>
        {t("invoice_membership_opt_out_section_intro")}
      </Typography>
      <Button
        variant="outlined"
        fullWidth
        onClick={() => {
          setError(null);
          setDialogOpen(true);
        }}
        sx={destructiveOutlineButtonSx}
      >
        {t("invoice_membership_opt_out_button")}
      </Button>

      <Dialog
        open={dialogOpen}
        onClose={closeDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "8px",
            bgcolor: getMm(isDarkMode).surfaceLowest,
            border: cardBorder,
            fontFamily: FONT,
          },
        }}
      >
        <DialogTitle sx={{ color: text, fontFamily: FONT, fontWeight: 700 }}>
          {t("invoice_membership_opt_out_confirm_title")}
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ color: muted, fontSize: "0.875rem", fontFamily: FONT }}>
            {t("invoice_membership_opt_out_confirm_body")}
          </Typography>
          {error ? (
            <Typography sx={{ color: "#EF4444", fontSize: "0.875rem", mt: 2, fontFamily: FONT }}>
              {error}
            </Typography>
          ) : null}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button onClick={closeDialog} disabled={submitting} sx={cancelButtonSx}>
            {t("cancel")}
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={submitting}
            variant="outlined"
            sx={destructiveOutlineButtonSx}
          >
            {submitting ? t("loading") : t("invoice_membership_opt_out_confirm")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

function MembershipPlanPicker({ invoice, token, onPlanSelected, isDarkMode }) {
  const { t } = useTranslation();
  const { primaryButtonSx } = useSystemButtonStyles();
  const isPeriodBilling = Array.isArray(invoice.billingPlans) && invoice.billingPlans.length > 0;
  const plans = (isPeriodBilling ? invoice.billingPlans : invoice.membershipPlans || []).filter(
    (p) => p.configured !== false
  );
  const [selectedPlan, setSelectedPlan] = useState(invoice.selectedPlan || plans[0]?.id || "");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const cardBorder = `1px solid ${getMm(isDarkMode).outlineVariant}`;
  const muted = getMm(isDarkMode).onSurfaceVariant;
  const text = getMm(isDarkMode).onSurface;

  const planLabelKey = invoicePlanLabelKey;

  const handleConfirm = async () => {
    if (!selectedPlan || !token) return;
    setSubmitting(true);
    setError(null);
    try {
      const updated = await selectInvoiceMembershipPlan(token, selectedPlan);
      if (typeof onPlanSelected === "function") await onPlanSelected(updated);
    } catch (err) {
      setError(err?.response?.data?.error || err.message || t("invoice_plan_select_error"));
    } finally {
      setSubmitting(false);
    }
  };

  if (plans.length === 0) {
    return (
      <InfoBanner variant="expired">{t("invoice_plan_not_configured")}</InfoBanner>
    );
  }

  const termPlans = plans.filter((p) => isTermPlanId(p.id));
  const monthlyPlans = plans.filter((p) => isMonthlyPlanId(p.id));
  const showGroupedPeriodPicker =
    isPeriodBilling && termPlans.length > 0 && monthlyPlans.length > 0;

  const renderPlanOption = (plan) => (
    <Box
      key={plan.id}
      onClick={() => setSelectedPlan(plan.id)}
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 1,
        p: 2,
        borderRadius: "8px",
        border: selectedPlan === plan.id ? "2px solid #374151" : cardBorder,
        cursor: "pointer",
        bgcolor: selectedPlan === plan.id
          ? getMm(isDarkMode).surfaceLow
          : getMm(isDarkMode).surfaceLowest,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0 }}>
        <Radio
          checked={selectedPlan === plan.id}
          size="small"
          sx={{ p: 0.5, color: muted, "&.Mui-checked": { color: getMm(isDarkMode).onSurfaceVariant } }}
        />
        <Typography sx={{ fontWeight: 600, color: text, fontFamily: FONT }}>
          {t(planLabelKey(plan.id))}
        </Typography>
      </Box>
      {selectedPlan === plan.id && planPaymentModeHint(plan.id, t) ? (
        <Typography sx={{ fontSize: "0.8125rem", color: muted, pl: 4.5, lineHeight: 1.5, fontFamily: FONT }}>
          {planPaymentModeHint(plan.id, t)}
        </Typography>
      ) : null}
    </Box>
  );

  return (
    <>
      <Typography sx={{ fontWeight: 700, color: text, mb: 1, fontSize: "1rem", fontFamily: FONT }}>
        {isPeriodBilling ? t("invoice_billing_plan_title") : t("invoice_membership_plan_title")}
      </Typography>
      {isPeriodBilling ? (
        <Typography sx={{ color: muted, mb: 2, fontSize: "0.875rem", lineHeight: 1.55, fontFamily: FONT }}>
          {t("invoice_billing_plan_step1_intro")}
        </Typography>
      ) : null}
      {!isPeriodBilling && invoice.membershipPhase ? (
        <Typography sx={{ color: muted, mb: 2, fontSize: "0.875rem", lineHeight: 1.55, fontFamily: FONT }}>
          {t("invoice_membership_plan_intro")}
        </Typography>
      ) : null}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, mb: 2.5 }}>
        {showGroupedPeriodPicker ? (
          <>
            <Typography sx={{ fontWeight: 700, fontSize: "0.8125rem", color: muted, fontFamily: FONT }}>
              {t("invoice_billing_plan_group_monthly")}
            </Typography>
            {monthlyPlans.map(renderPlanOption)}
            <Typography
              sx={{ fontWeight: 700, fontSize: "0.8125rem", color: muted, fontFamily: FONT, mt: 0.5 }}
            >
              {t("invoice_billing_plan_group_term")}
            </Typography>
            {termPlans.map(renderPlanOption)}
          </>
        ) : (
          [...monthlyPlans, ...termPlans, ...plans.filter((p) => !isMonthlyPlanId(p.id) && !isTermPlanId(p.id))].map(
            renderPlanOption
          )
        )}
      </Box>
      {error ? (
        <Typography sx={{ color: "#EF4444", fontSize: "0.875rem", mb: 2, fontFamily: FONT }}>{error}</Typography>
      ) : null}
      <Button
        fullWidth
        variant="contained"
        disabled={submitting || !selectedPlan}
        onClick={handleConfirm}
        sx={{ ...primaryButtonSx, py: 1.25, fontWeight: 700, mb: 2.5 }}
      >
        {submitting ? t("loading") : t("invoice_membership_plan_confirm")}
      </Button>
      <Divider sx={{ borderColor: getMm(isDarkMode).outlineVariant, mb: 2 }} />
    </>
  );
}

function invoiceNeedsMonthlySubscriptionAck(invoice) {
  return invoice.selectedPlanKind === "monthly" && invoice.subscriptionRecurringMonthlyAmount != null;
}

/** Line items + proration + monthly subscription ack (no separate confirm step). */
function InvoiceBreakdownReview({
  invoice,
  isDarkMode,
  monthlySubscriptionAck,
  onMonthlySubscriptionAckChange,
  token,
  termsAccepted,
  onTermsAcceptedChange,
}) {
  const { t } = useTranslation();
  const cardBorder = `1px solid ${getMm(isDarkMode).outlineVariant}`;
  const muted = getMm(isDarkMode).onSurfaceVariant;
  const text = getMm(isDarkMode).onSurface;

  const needsMonthlySubscriptionAck = invoiceNeedsMonthlySubscriptionAck(invoice);
  const periodEnd = formatBillingPeriodEnd(invoice.periodEnd);

  return (
    <>
      <Typography sx={{ fontWeight: 700, color: text, mb: 1, fontSize: "1rem", fontFamily: FONT }}>
        {t("invoice_breakdown_step_title")}
      </Typography>
      <Typography sx={{ color: muted, fontSize: "0.875rem", mb: 2, fontFamily: FONT, lineHeight: 1.55 }}>
        {invoice.selectedPlanKind === "monthly"
          ? t("invoice_breakdown_step_intro_monthly")
          : t("invoice_breakdown_step_intro_term")}
      </Typography>

      {invoice.selectedPlan ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 2,
            mb: 2,
            p: 1.5,
            borderRadius: "8px",
            border: cardBorder,
            bgcolor: getMm(isDarkMode).surfaceLow,
          }}
        >
          <Typography sx={{ fontWeight: 600, color: text, fontSize: "0.875rem", fontFamily: FONT }}>
            {t("invoice_selected_plan_label", { plan: t(invoicePlanLabelKey(invoice.selectedPlan)) })}
          </Typography>
        </Box>
      ) : null}

      <InvoiceItemizedCharges invoice={invoice} isDarkMode={isDarkMode} showTotal />

      <InvoiceTermProrationBanner invoice={invoice} isDarkMode={isDarkMode} variant="breakdown" />

      {needsMonthlySubscriptionAck ? (
        <InvoiceSubscriptionSummaryBanner invoice={invoice} isDarkMode={isDarkMode} variant="breakdown" />
      ) : null}

      {needsMonthlySubscriptionAck ? (
        <Box
          sx={{
            mb: 2,
            p: 2,
            borderRadius: "8px",
            border: cardBorder,
            bgcolor: getMm(isDarkMode).surfaceLowest,
          }}
        >
          <FormControlLabel
            control={
              <Checkbox
                checked={monthlySubscriptionAck}
                onChange={(e) => onMonthlySubscriptionAckChange(e.target.checked)}
                sx={{
                  color: muted,
                  "&.Mui-checked": { color: getMm(isDarkMode).onSurfaceVariant },
                }}
              />
            }
            label={
              <Typography sx={{ color: text, fontSize: "0.875rem", lineHeight: 1.55, fontFamily: FONT }}>
                {t("invoice_breakdown_monthly_subscription_ack", {
                  recurring: formatMoney(invoice.subscriptionRecurringMonthlyAmount, invoice.currency),
                  periodEnd: periodEnd || "—",
                })}
              </Typography>
            }
            sx={{ alignItems: "flex-start", m: 0, "& .MuiCheckbox-root": { pt: 0.25 } }}
          />
          {!monthlySubscriptionAck ? (
            <Typography sx={{ color: muted, fontSize: "0.8125rem", mt: 1, ml: 4.5, fontFamily: FONT }}>
              {t("invoice_breakdown_monthly_ack_required")}
            </Typography>
          ) : null}
        </Box>
      ) : null}

      {invoice.hasInvoiceTerms && typeof onTermsAcceptedChange === "function" ? (
        <InvoiceTermsAcceptance
          invoice={invoice}
          token={token}
          isDarkMode={isDarkMode}
          termsAccepted={termsAccepted}
          onTermsAcceptedChange={onTermsAcceptedChange}
          cardBorder={cardBorder}
          muted={muted}
          text={text}
        />
      ) : null}
    </>
  );
}

function InvoiceTermsAcceptance({
  invoice,
  token,
  isDarkMode,
  termsAccepted,
  onTermsAcceptedChange,
  cardBorder,
  muted,
  text,
  showRequiredHint = true,
}) {
  const { t } = useTranslation();
  const termsUrl = token ? `/invoice/${encodeURIComponent(token)}/terms` : null;

  return (
    <Box
      sx={{
        mb: 2,
        p: 2,
        borderRadius: "8px",
        border: cardBorder,
        bgcolor: getMm(isDarkMode).surfaceLowest,
      }}
    >
      <FormControlLabel
        control={
          <Checkbox
            checked={Boolean(termsAccepted)}
            onChange={(e) => onTermsAcceptedChange(e.target.checked)}
            sx={{
              color: muted,
              "&.Mui-checked": { color: getMm(isDarkMode).onSurfaceVariant },
            }}
          />
        }
        label={
          <Typography sx={{ color: text, fontSize: "0.875rem", fontFamily: FONT }}>
            {t("invoice_terms_accept_prefix")}{" "}
            {termsUrl ? (
              <Box
                component="a"
                href={termsUrl}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  color: getMm(isDarkMode).primary,
                  fontWeight: 600,
                  textDecoration: "underline",
                }}
              >
                {t("invoice_terms_link_label")}
              </Box>
            ) : (
              t("invoice_terms_link_label")
            )}
          </Typography>
        }
        sx={{ alignItems: "flex-start", m: 0, "& .MuiCheckbox-root": { pt: 0.25 } }}
      />
      {invoice.subscriptionCheckout ? (
        <Typography sx={{ color: muted, fontSize: "0.8125rem", mt: 1.5, lineHeight: 1.5, fontFamily: FONT }}>
          {t("invoice_terms_subscription_note")}
        </Typography>
      ) : null}
      {showRequiredHint && !termsAccepted ? (
        <Typography sx={{ color: muted, fontSize: "0.8125rem", mt: 1, ml: 4.5, fontFamily: FONT }}>
          {t("invoice_terms_accept_required")}
        </Typography>
      ) : null}
    </Box>
  );
}

function paymentMethodLabelKey(id) {
  if (id === "stripe") return "payment_method_stripe";
  if (id === "swish") return "payment_method_swish";
  if (id === "bank_transfer") return "payment_method_bank_transfer";
  if (id === "cash") return "payment_method_cash";
  return "invoice_payment_method";
}

function paymentMethodHintKey(id) {
  if (id === "stripe") return "invoice_stripe_hint";
  if (id === "swish") return "invoice_swish_hint";
  if (id === "bank_transfer") return "invoice_bank_transfer_hint";
  if (id === "cash") return "invoice_cash_hint";
  return "invoice_payment_method";
}

function paymentMethodContinueLabelKey(id) {
  if (id === "stripe") return "invoice_pay_now";
  if (id === "swish") return "invoice_continue_swish";
  if (id === "bank_transfer") return "invoice_continue_bank_transfer";
  if (id === "cash") return "invoice_continue_cash";
  return "invoice_continue_payment";
}

function paymentMethodManualConfirmKey(id) {
  if (id === "swish") return "invoice_manual_confirmed_swish";
  if (id === "bank_transfer") return "invoice_manual_confirmed_bank_transfer";
  if (id === "cash") return "invoice_manual_confirmed_cash";
  return "invoice_manual_confirmed_generic";
}

function paymentMethodManualConfirmTitleKey(id) {
  if (id === "cash") return "invoice_manual_confirmed_cash_title";
  if (id === "swish") return "invoice_manual_confirmed_swish_title";
  if (id === "bank_transfer") return "invoice_manual_confirmed_bank_title";
  return "invoice_manual_confirmed_title";
}

function ManualPaymentInstructions({
  methodId,
  method,
  invoice,
  isDarkMode,
  cardBorder,
  muted,
  text,
  t,
}) {
  if (methodId === "swish" && method?.swishNumber) {
    return (
      <Box
        sx={{
          mb: 2.5,
          p: 2,
          borderRadius: "8px",
          border: cardBorder,
          bgcolor: getMm(isDarkMode).surfaceLowest,
        }}
      >
        <Typography sx={{ fontWeight: 700, color: text, mb: 1, fontFamily: FONT }}>
          {t("invoice_swish_instructions_title")}
        </Typography>
        <Typography sx={{ color: muted, mb: 1, fontFamily: FONT }}>
          {t("invoice_swish_instructions_body", {
            amount: formatMoney(invoice.amount, invoice.currency),
            number: method.swishNumber,
          })}
        </Typography>
        <Typography sx={{ fontWeight: 700, color: text, fontFamily: FONT }}>{method.swishNumber}</Typography>
      </Box>
    );
  }

  if (methodId === "bank_transfer" && method?.bankTransferInstructions) {
    return (
      <Box
        sx={{
          mb: 2.5,
          p: 2,
          borderRadius: "8px",
          border: cardBorder,
          bgcolor: getMm(isDarkMode).surfaceLowest,
        }}
      >
        <Typography sx={{ fontWeight: 700, color: text, mb: 1, fontFamily: FONT }}>
          {t("invoice_bank_transfer_instructions_title")}
        </Typography>
        <Typography sx={{ color: text, whiteSpace: "pre-wrap", fontFamily: FONT }}>
          {method.bankTransferInstructions}
        </Typography>
      </Box>
    );
  }

  if (methodId === "cash" && method?.ready !== false) {
    return (
      <Box
        sx={{
          mb: 2.5,
          p: 2,
          borderRadius: "8px",
          border: cardBorder,
          bgcolor: getMm(isDarkMode).surfaceLowest,
        }}
      >
        <Typography sx={{ fontWeight: 700, color: text, mb: 1, fontFamily: FONT }}>
          {t("invoice_cash_instructions_title")}
        </Typography>
        <Typography sx={{ color: muted, mb: method?.cashInstructions ? 1 : 0, fontFamily: FONT }}>
          {t("invoice_cash_instructions_body", {
            amount: formatMoney(invoice.amount, invoice.currency),
          })}
        </Typography>
        {method?.cashInstructions ? (
          <Typography sx={{ color: text, whiteSpace: "pre-wrap", fontFamily: FONT }}>
            {method.cashInstructions}
          </Typography>
        ) : null}
      </Box>
    );
  }

  return null;
}

function PaymentMethodIcon({ id, isDarkMode }) {
  const sx = { color: getMm(isDarkMode).onSurfaceVariant, fontSize: 22 };
  if (id === "swish") return <PhoneIcon sx={sx} />;
  if (id === "bank_transfer") return <AccountBalanceIcon sx={sx} />;
  if (id === "cash") return <PaymentsIcon sx={sx} />;
  return <CreditCardIcon sx={sx} />;
}

function InvoicePaymentMethodSection({
  invoice,
  token,
  selectedMethod,
  onSelectMethod,
  onContinuePayment,
  paying,
  manualPaymentConfirmed,
  isDarkMode,
  cardBorder,
  muted,
  text,
  primaryButtonSx,
  monthlySubscriptionAck = true,
  pendingBreakdownConfirmation = false,
  termsAccepted: termsAcceptedProp,
  onTermsAcceptedChange,
  termsAcceptanceInBreakdown = false,
}) {
  const { t } = useTranslation();
  const [termsAcceptedLocal, setTermsAcceptedLocal] = useState(false);
  const termsControlled = typeof termsAcceptedProp === "boolean" && typeof onTermsAcceptedChange === "function";
  const termsAccepted = termsControlled ? termsAcceptedProp : termsAcceptedLocal;
  const setTermsAccepted = termsControlled ? onTermsAcceptedChange : setTermsAcceptedLocal;
  const hasInvoiceTerms = Boolean(invoice.hasInvoiceTerms);

  const methods = useMemo(() => {
    if (Array.isArray(invoice.paymentMethods) && invoice.paymentMethods.length > 0) {
      return invoice.paymentMethods;
    }
    return [{ id: "stripe", ready: true }];
  }, [invoice.paymentMethods]);

  useEffect(() => {
    if (selectedMethod && !methods.some((method) => method.id === selectedMethod)) {
      onSelectMethod(null);
    }
  }, [methods, selectedMethod, onSelectMethod]);

  const selected = methods.find((method) => method.id === selectedMethod) || null;
  const mustAcceptTerms = hasInvoiceTerms && Boolean(selectedMethod);
  const needsMonthlyAck = pendingBreakdownConfirmation && invoiceNeedsMonthlySubscriptionAck(invoice);
  const canProceed =
    Boolean(selected?.ready) &&
    (!mustAcceptTerms || termsAccepted) &&
    (!needsMonthlyAck || monthlySubscriptionAck);

  const handleContinue = () => {
    if (!selectedMethod || !canProceed || paying) return;
    onContinuePayment(selectedMethod, hasInvoiceTerms ? termsAccepted : false);
  };

  if (manualPaymentConfirmed) {
    const confirmedId = manualPaymentConfirmed;
    const confirmedMethod =
      methods.find((m) => m.id === confirmedId) || { id: confirmedId, ready: true };

    return (
      <Box sx={{ pt: 0.5 }}>
        <Box
          sx={{
            textAlign: "center",
            mb: 2.5,
            py: 2,
            px: 2,
            borderRadius: "8px",
            border: cardBorder,
            bgcolor: isDarkMode ? "rgba(76, 175, 80, 0.1)" : "#ECFDF5",
          }}
        >
          <CheckCircleIcon sx={{ color: "#4CAF50", fontSize: 48, mb: 1 }} />
          <Typography
            sx={{
              fontWeight: 800,
              fontSize: { xs: "1.25rem", sm: "1.375rem" },
              color: text,
              mb: 1,
              fontFamily: FONT,
            }}
          >
            {t(paymentMethodManualConfirmTitleKey(confirmedId))}
          </Typography>
          <Typography
            sx={{
              color: muted,
              fontSize: "0.9375rem",
              lineHeight: 1.55,
              maxWidth: 480,
              mx: "auto",
              fontFamily: FONT,
            }}
          >
            {t(paymentMethodManualConfirmKey(confirmedId))}
          </Typography>
        </Box>
        <ManualPaymentInstructions
          methodId={confirmedId}
          method={confirmedMethod}
          invoice={invoice}
          isDarkMode={isDarkMode}
          cardBorder={cardBorder}
          muted={muted}
          text={text}
          t={t}
        />
      </Box>
    );
  }

  return (
    <>
      <Typography sx={{ fontWeight: 700, color: text, mb: 1.5, fontFamily: FONT }}>
        {t("invoice_payment_method")}
      </Typography>
      <Typography sx={{ fontSize: "0.875rem", color: muted, mb: 1.5, fontFamily: FONT }}>
        {invoice.subscriptionCheckout
          ? t("invoice_payment_method_subscription_hint")
          : invoice.stripeOnlyPayment
            ? t("invoice_payment_method_stripe_only_hint")
            : t("invoice_payment_method_select_hint")}
      </Typography>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25, mb: 2.5 }}>
        {methods.map((method) => {
          const isSelected = selectedMethod === method.id;
          const disabled = !method.ready;
          return (
            <Box
              key={method.id}
              onClick={() => {
                if (!disabled) onSelectMethod(method.id);
              }}
              sx={{
                display: "flex",
                alignItems: "flex-start",
                gap: 1.5,
                p: 2,
                borderRadius: "8px",
                border: isSelected ? "2px solid #374151" : cardBorder,
                cursor: disabled ? "not-allowed" : "pointer",
                opacity: disabled ? 0.55 : 1,
                bgcolor: isSelected
                  ? (getMm(isDarkMode).surfaceLow)
                  : (getMm(isDarkMode).surfaceLowest),
                transition: "border-color 0.15s ease, background-color 0.15s ease",
                "&:hover": disabled ? undefined : { borderColor: "#374151" },
              }}
            >
              <Radio
                checked={isSelected}
                disabled={disabled}
                size="small"
                sx={{
                  p: 0.5,
                  mt: 0.25,
                  color: muted,
                  "&.Mui-checked": { color: getMm(isDarkMode).onSurfaceVariant },
                }}
              />
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: "8px",
                  bgcolor: getMm(isDarkMode).surfaceContainer,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <PaymentMethodIcon id={method.id} isDarkMode={isDarkMode} />
              </Box>
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography sx={{ fontWeight: 600, color: text, fontFamily: FONT }}>
                  {t(paymentMethodLabelKey(method.id))}
                </Typography>
                <Typography sx={{ fontSize: "0.8125rem", color: muted, fontFamily: FONT }}>
                  {t(paymentMethodHintKey(method.id))}
                </Typography>
                {method.id === "stripe" ? <PaymentBrandLogos isDarkMode={isDarkMode} /> : null}
                {!method.ready && method.unavailableReason ? (
                  <Typography sx={{ fontSize: "0.8125rem", color: "#F59E0B", mt: 0.5, fontFamily: FONT }}>
                    {t(`invoice_payment_method_unavailable_${method.unavailableReason}`, {
                      defaultValue: t("invoice_payment_method_unavailable"),
                    })}
                  </Typography>
                ) : null}
              </Box>
            </Box>
          );
        })}
      </Box>

      <ManualPaymentInstructions
        methodId={selectedMethod}
        method={selected}
        invoice={invoice}
        isDarkMode={isDarkMode}
        cardBorder={cardBorder}
        muted={muted}
        text={text}
        t={t}
      />

      {mustAcceptTerms && !termsAcceptanceInBreakdown ? (
        <InvoiceTermsAcceptance
          invoice={invoice}
          token={token}
          isDarkMode={isDarkMode}
          termsAccepted={termsAccepted}
          onTermsAcceptedChange={setTermsAccepted}
          cardBorder={cardBorder}
          muted={muted}
          text={text}
          showRequiredHint={Boolean(selectedMethod)}
        />
      ) : null}

      {selected?.ready && !manualPaymentConfirmed ? (
        <Button
          fullWidth
          variant="contained"
          color="primary"
          disabled={paying || !canProceed}
          onClick={handleContinue}
          sx={{ ...primaryButtonSx, py: 1.5, fontSize: "1rem", fontWeight: 700 }}
        >
          {paying ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <CircularProgress size={20} sx={{ color: "#fff" }} />
              {t("loading")}
            </Box>
          ) : (
            t(paymentMethodContinueLabelKey(selectedMethod))
          )}
        </Button>
      ) : null}
    </>
  );
}

function InvoicePageBody({
  invoice,
  token,
  onContinuePayment,
  paying,
  manualPaymentConfirmed,
  onClearManualConfirmation,
  onReload,
}) {
  const { t } = useTranslation();
  const { isDarkMode } = useTheme();
  const {
    primaryButtonSx,
    cancelButtonSx,
    outlineButtonSx,
    secondaryButtonSx,
    destructiveButtonSx,
    destructiveOutlineButtonSx,
    greenSmileyButtonSx,
    redSmileyButtonSx,
    iconActionSx,
    iconActionTableSx,
    iconActionLargeSx,
    toolbarButtonSx,
    dialogPrimarySx,
    dialogCancelSx,
    dialogFormPrimarySx,
    dialogFormCancelSx,
    dialogFormDestructiveSx,
    warningButtonSx,
    buttonSx,
  } = useSystemButtonStyles();
  const [selectedMethod, setSelectedMethod] = useState(null);
  const [clearingPlan, setClearingPlan] = useState(false);
  const [changePlanError, setChangePlanError] = useState(null);
  const [monthlySubscriptionAck, setMonthlySubscriptionAck] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);

  const expired = invoice.status === "expired";
  const paid = invoice.isPaid || invoice.status === "paid";
  const revoked = invoice.status === "revoked";
  const isMembershipInvoice =
    invoice.invoiceKind === "membership_conversion" ||
    invoice.invoiceKind === "membership_renewal" ||
    invoice.membershipPhase === "awaiting_plan" ||
    invoice.membershipPhase === "lapsed" ||
    Boolean(invoice.requiresPlanSelection) ||
    (Array.isArray(invoice.membershipPlans) && invoice.membershipPlans.length > 0);
  const hasPeriodBillingPlans =
    Array.isArray(invoice.billingPlans) && invoice.billingPlans.filter((p) => p.configured !== false).length > 0;
  const invoiceAmount = parseFloat(invoice.amount) || 0;
  const needsPlanSelection =
    !paid &&
    !expired &&
    !revoked &&
    (Boolean(invoice.requiresPlanSelection) ||
      (hasPeriodBillingPlans &&
        invoice.billingPlans.filter((p) => p.configured !== false).length > 1 &&
        invoiceAmount <= 0 &&
        !invoice.selectedPlan) ||
      (isMembershipInvoice && invoiceAmount <= 0 && !invoice.selectedPlan));
  const needsBreakdownConfirmation =
    !paid && !expired && !revoked && !needsPlanSelection && Boolean(invoice.requiresBreakdownConfirmation);
  const awaitingPaymentSetup =
    !paid && !expired && !revoked && invoiceAmount <= 0 && !needsPlanSelection && !needsBreakdownConfirmation;
  const canShowPayment =
    !expired &&
    !paid &&
    !revoked &&
    invoiceAmount > 0 &&
    !needsPlanSelection;
  const { hasStructured: hasStructuredLineItems } = resolveInvoiceLineGroups(invoice);
  const availablePlanCount = Math.max(
    (invoice.billingPlans || []).filter((p) => p.configured !== false).length,
    (invoice.membershipPlans || []).filter((p) => p.configured !== false).length
  );
  const canChangePlan =
    Boolean(invoice.selectedPlan) &&
    availablePlanCount > 1 &&
    canShowPayment &&
    !needsPlanSelection;

  const handleChangePlan = async () => {
    if (!token || clearingPlan) return;
    setClearingPlan(true);
    setChangePlanError(null);
    try {
      await clearInvoicePlanSelection(token);
      if (typeof onReload === "function") await onReload();
    } catch (err) {
      setChangePlanError(err?.response?.data?.error || err.message || t("invoice_change_plan_error"));
    } finally {
      setClearingPlan(false);
    }
  };

  useEffect(() => {
    if (manualPaymentConfirmed) {
      setSelectedMethod(manualPaymentConfirmed);
      return;
    }
    const methods =
      Array.isArray(invoice.paymentMethods) && invoice.paymentMethods.length > 0
        ? invoice.paymentMethods
        : [{ id: "stripe", ready: true }];
    const onlyReady = methods.length === 1 && methods[0].ready ? methods[0].id : null;
    setSelectedMethod(onlyReady);
  }, [
    invoice.token,
    invoice.selectedPlan,
    invoice.amount,
    invoice.paymentMethods,
    needsPlanSelection,
    manualPaymentConfirmed,
  ]);

  useEffect(() => {
    setMonthlySubscriptionAck(false);
    setTermsAccepted(false);
  }, [invoice.token, invoice.selectedPlan, invoice.amount, needsBreakdownConfirmation]);

  const handleSelectMethod = (method) => {
    if (manualPaymentConfirmed) return;
    setSelectedMethod(method);
    if (onClearManualConfirmation) {
      onClearManualConfirmation();
    }
  };

  const cardBorder = `1px solid ${getMm(isDarkMode).outlineVariant}`;
  const muted = getMm(isDarkMode).onSurfaceVariant;
  const text = getMm(isDarkMode).onSurface;

  return (
    <InvoiceShell maxWidth={680}>
      <Card
        sx={{
          width: "100%",
          borderRadius: "8px",
          boxShadow: "none",
          border: cardBorder,
          bgcolor: getMm(isDarkMode).surfaceLowest,
          overflow: "hidden",
          fontFamily: FONT,
        }}
      >
        {/* Header band — matches NewEra email / admin accent */}
        <Box
          sx={{
            bgcolor: "#374151",
            px: { xs: 2.5, sm: 3.5 },
            py: { xs: 2, sm: 2.5 },
            display: "flex",
            alignItems: "center",
            gap: 1.5,
          }}
        >
          <ReceiptLongIcon sx={{ color: "#FFFFFF", fontSize: 28, opacity: 0.9 }} />
          <Box sx={{ minWidth: 0 }}>
            <Typography
              sx={{
                color: "#FFFFFF",
                fontWeight: 700,
                fontSize: { xs: "1.125rem", sm: "1.25rem" },
                lineHeight: 1.2,
                fontFamily: FONT,
              }}
            >
              {t("invoice_title")}
            </Typography>
            <Typography
              sx={{
                color: "rgba(255,255,255,0.85)",
                fontSize: "0.875rem",
                fontWeight: 500,
                mt: 0.25,
                fontFamily: FONT,
              }}
            >
              {invoice.clubName}
            </Typography>
          </Box>
        </Box>

        <CardContent sx={{ p: { xs: 2.5, sm: 3.5 } }}>
          {invoice.periodName && (
            <Typography sx={{ color: muted, fontSize: "0.875rem", mb: 2, fontFamily: FONT }}>
              {invoice.periodName}
              {invoice.periodStart && invoice.periodEnd
                ? ` · ${invoice.periodStart} – ${invoice.periodEnd}`
                : ""}
            </Typography>
          )}

          {/* Member details */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
              gap: 2,
              mb: 2.5,
              p: 2,
              borderRadius: "8px",
              bgcolor: getMm(isDarkMode).surfaceLowest,
              border: cardBorder,
            }}
          >
            <Box>
              <Typography sx={{ fontSize: "0.6875rem", fontWeight: 700, color: muted, textTransform: "uppercase", letterSpacing: "0.06em", mb: 0.5, fontFamily: FONT }}>
                {t("name")}
              </Typography>
              <Typography sx={{ fontWeight: 600, color: text, fontFamily: FONT }}>{invoice.memberName || "—"}</Typography>
            </Box>
            {invoice.memberEmail && (
              <Box>
                <Typography sx={{ fontSize: "0.6875rem", fontWeight: 700, color: muted, textTransform: "uppercase", letterSpacing: "0.06em", mb: 0.5, fontFamily: FONT }}>
                  {t("email")}
                </Typography>
                <Typography sx={{ color: getMm(isDarkMode).onSurfaceVariant, fontSize: "0.9375rem", wordBreak: "break-word", fontFamily: FONT }}>
                  {invoice.memberEmail}
                </Typography>
              </Box>
            )}
          </Box>

          {paid ? (
            <InfoBanner variant="paid">{t("invoice_already_paid")}</InfoBanner>
          ) : expired ? (
            <InfoBanner variant="expired">{t("invoice_expired")}</InfoBanner>
          ) : revoked ? (
            <InfoBanner variant="expired">
              {isMembershipInvoice
                ? t("invoice_membership_opt_out_done")
                : t("invoice_revoked")}
            </InfoBanner>
          ) : (
            <InfoBanner variant="active">
              {isMembershipInvoice && invoice.trialEndsAt
                ? t("invoice_membership_trial_ended_banner", { date: formatDate(invoice.trialEndsAt) })
                : t("invoice_valid_until", { date: formatDate(invoice.expiresAt) })}
            </InfoBanner>
          )}

          {needsPlanSelection ? (
            <MembershipPlanPicker
              invoice={invoice}
              token={token}
              onPlanSelected={async () => {
                if (typeof onReload === "function") await onReload();
              }}
              isDarkMode={isDarkMode}
            />
          ) : null}

          {awaitingPaymentSetup ? (
            <InfoBanner variant="expired">{t("invoice_awaiting_payment_setup")}</InfoBanner>
          ) : null}

          {!needsPlanSelection ? (
            <>
          {canChangePlan ? (
            <Box sx={{ mb: 2 }}>
              <Button
                variant="outlined"
                size="small"
                startIcon={clearingPlan ? <CircularProgress size={16} /> : <ArrowBackIcon />}
                onClick={handleChangePlan}
                disabled={clearingPlan}
                sx={{ ...outlineButtonSx, fontFamily: FONT, textTransform: "none", fontWeight: 600 }}
              >
                {t("invoice_change_plan")}
              </Button>
              {invoice.selectedPlan ? (
                <Typography sx={{ mt: 1, fontSize: "0.8125rem", color: muted, fontFamily: FONT }}>
                  {t("invoice_selected_plan_label", { plan: t(invoicePlanLabelKey(invoice.selectedPlan)) })}
                </Typography>
              ) : null}
              {changePlanError ? (
                <Typography sx={{ mt: 1, fontSize: "0.8125rem", color: "#EF4444", fontFamily: FONT }}>
                  {changePlanError}
                </Typography>
              ) : null}
            </Box>
          ) : null}

          {needsBreakdownConfirmation ? (
            <InvoiceBreakdownReview
              invoice={invoice}
              isDarkMode={isDarkMode}
              monthlySubscriptionAck={monthlySubscriptionAck}
              onMonthlySubscriptionAckChange={setMonthlySubscriptionAck}
              token={token}
              termsAccepted={termsAccepted}
              onTermsAcceptedChange={setTermsAccepted}
            />
          ) : (
            <>
              <Divider sx={{ borderColor: getMm(isDarkMode).outlineVariant, mb: 2 }} />

              <Typography sx={{ fontWeight: 700, color: text, mb: 0.75, fontSize: "1rem", fontFamily: FONT }}>
                {t("invoice_line_items")}
              </Typography>
              {hasStructuredLineItems ? (
                <Typography sx={{ color: muted, fontSize: "0.875rem", mb: 1.5, lineHeight: 1.55, fontFamily: FONT }}>
                  {t("invoice_payment_charges_intro")}
                </Typography>
              ) : null}

              <InvoiceTermProrationBanner invoice={invoice} isDarkMode={isDarkMode} variant="payment" />

              <InvoiceSubscriptionSummaryBanner invoice={invoice} isDarkMode={isDarkMode} variant="payment" />

              <InvoiceItemizedCharges invoice={invoice} isDarkMode={isDarkMode} showTotal />
            </>
          )}

          {canShowPayment && (
            <InvoicePaymentMethodSection
              invoice={invoice}
              token={token}
              selectedMethod={selectedMethod}
              onSelectMethod={handleSelectMethod}
              onContinuePayment={onContinuePayment}
              paying={paying}
              manualPaymentConfirmed={manualPaymentConfirmed}
              isDarkMode={isDarkMode}
              cardBorder={cardBorder}
              muted={muted}
              text={text}
              primaryButtonSx={primaryButtonSx}
              monthlySubscriptionAck={monthlySubscriptionAck}
              pendingBreakdownConfirmation={needsBreakdownConfirmation}
              termsAccepted={termsAccepted}
              onTermsAcceptedChange={setTermsAccepted}
              termsAcceptanceInBreakdown={needsBreakdownConfirmation && Boolean(invoice.hasInvoiceTerms)}
            />
          )}

          {!paid && !expired && !revoked && !isMembershipInvoice ? (
            <InvoiceOptOutSection invoice={invoice} token={token} onOptOutComplete={onReload} />
          ) : null}
            </>
          ) : null}

          {!paid && !expired && !revoked && isMembershipInvoice ? (
            <MembershipInvoiceOptOutSection token={token} onOptOutComplete={onReload} />
          ) : null}
        </CardContent>
      </Card>

      {token && (
        <Typography sx={{ textAlign: "center", mt: 1.5, fontSize: "0.6875rem", color: muted, fontFamily: FONT }}>
          {t("invoice_ref")}: {token.slice(0, 8)}…
        </Typography>
      )}
    </InvoiceShell>
  );
}

function InvoicePageContent() {
  const { token } = useParams();
  const [searchParams] = useSearchParams();
  const { t, i18n } = useTranslation();
  usePublicPageLanguage();
  const { isDarkMode } = useTheme();
  const {
    primaryButtonSx,
    cancelButtonSx,
    outlineButtonSx,
    secondaryButtonSx,
    destructiveButtonSx,
    destructiveOutlineButtonSx,
    greenSmileyButtonSx,
    redSmileyButtonSx,
    iconActionSx,
    iconActionTableSx,
    iconActionLargeSx,
    toolbarButtonSx,
    dialogPrimarySx,
    dialogCancelSx,
    dialogFormPrimarySx,
    dialogFormCancelSx,
    dialogFormDestructiveSx,
    warningButtonSx,
    buttonSx,
  } = useSystemButtonStyles();
  const isSuccessRoute = window.location.pathname.endsWith("/success");
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [paying, setPaying] = useState(false);
  const [manualPaymentConfirmed, setManualPaymentConfirmed] = useState(null);
  const [confirmingPayment, setConfirmingPayment] = useState(false);
  const [paymentConfirmed, setPaymentConfirmed] = useState(false);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError(null);
    try {
      const data = await fetchPublicInvoice(token);
      setInvoice(data);
      if (data?.pendingPaymentMethod && data.status !== "paid" && !data.isPaid) {
        setManualPaymentConfirmed(data.pendingPaymentMethod);
      }
    } catch (err) {
      const status = err?.response?.status;
      if (status === 404) setError("not_found");
      else if (status === 410) setError("expired");
      else setError(err?.response?.data?.error || err.message || "error");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!isSuccessRoute || !token) return undefined;
    const sessionId = searchParams.get("session_id");
    if (!sessionId) {
      setPaymentConfirmed(true);
      return undefined;
    }

    let cancelled = false;
    setConfirmingPayment(true);
    setPaymentConfirmed(false);
    setError(null);

    verifyInvoiceCheckout(token, sessionId)
      .then(async () => {
        if (cancelled) return;
        await load();
        setPaymentConfirmed(true);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err?.response?.data?.error || err.message || t("finance_error_stripe_start"));
        setPaymentConfirmed(false);
      })
      .finally(() => {
        if (!cancelled) setConfirmingPayment(false);
      });

    return () => {
      cancelled = true;
    };
  }, [isSuccessRoute, token, searchParams, load, t]);

  useEffect(() => {
    setManualPaymentConfirmed(null);
  }, [token]);

  const handleContinuePayment = async (paymentMethod, termsAccepted) => {
    setPaying(true);
    setError(null);
    try {
      if (invoice?.requiresBreakdownConfirmation) {
        await confirmInvoiceBreakdown(token);
      }
      const data = await createInvoiceCheckout(token, paymentMethod, {
        termsAccepted: termsAccepted === true,
        locale: i18n.language,
      });
      if (data?.url) {
        window.location.href = data.url;
        return;
      }
      if (data?.manual) {
        setManualPaymentConfirmed(paymentMethod);
        await load();
        return;
      }
      setError(t("finance_error_no_payment_url"));
    } catch (err) {
      setError(err?.response?.data?.error || err.message || t("finance_error_stripe_start"));
    } finally {
      setPaying(false);
    }
  };

  const successView = useMemo(() => {
    if (!isSuccessRoute) return false;
    if (searchParams.get("session_id")) {
      return paymentConfirmed && !confirmingPayment && !error;
    }
    return Boolean(invoice?.isPaid);
  }, [isSuccessRoute, searchParams, invoice, paymentConfirmed, confirmingPayment, error]);

  if (loading || (isSuccessRoute && confirmingPayment)) {
    return (
      <InvoiceShell maxWidth={480}>
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2, py: 4 }}>
          <CircularProgress sx={{ color: getMm(isDarkMode).onSurfaceVariant }} />
          <Typography sx={{ color: getMm(isDarkMode).onSurfaceVariant, fontFamily: FONT }}>{t("loading")}</Typography>
        </Box>
      </InvoiceShell>
    );
  }

  if (successView || (invoice?.isPaid && isSuccessRoute)) {
    return (
      <InvoiceShell maxWidth={480}>
        <StatusCard
          icon={
            <Box
              sx={{
                width: 72,
                height: 72,
                borderRadius: "50%",
                bgcolor: "#4CAF50",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mx: "auto",
                mb: 2,
              }}
            >
              <CheckCircleIcon sx={{ fontSize: 40, color: "#fff" }} />
            </Box>
          }
          title={t("finance_payment_received")}
          body={t("finance_payment_thank_you")}
        />
      </InvoiceShell>
    );
  }

  if (error === "not_found") {
    return (
      <InvoiceShell maxWidth={480}>
        <StatusCard
          icon={<ErrorOutlineIcon sx={{ fontSize: 56, color: "#EF4444", mb: 2 }} />}
          title={t("invoice_not_found_title")}
          body={t("invoice_not_found_body")}
        />
      </InvoiceShell>
    );
  }

  if (typeof error === "string" && error) {
    return (
      <InvoiceShell maxWidth={480}>
        <StatusCard
          icon={<ErrorOutlineIcon sx={{ fontSize: 56, color: "#EF4444", mb: 2 }} />}
          title={t("error")}
          body={error}
          action={
            <Button onClick={load} variant="contained" sx={primaryButtonSx}>
              {t("try_again")}
            </Button>
          }
        />
      </InvoiceShell>
    );
  }

  if (!invoice) return null;

  return (
    <InvoicePageBody
      invoice={invoice}
      token={token}
      onContinuePayment={handleContinuePayment}
      paying={paying}
      manualPaymentConfirmed={manualPaymentConfirmed}
      onClearManualConfirmation={() => setManualPaymentConfirmed(null)}
      onReload={load}
    />
  );
}

export default function InvoicePage() {
  return (
    <CustomThemeProvider>
      <InvoicePageContent />
    </CustomThemeProvider>
  );
}
